import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, ReferenceLine, ResponsiveContainer
} from 'recharts';
import { getLiveTraffic } from '../../../store';
import { UPTIME_HISTORY } from '../../../configuration/paths';
import '../styles/trafficMetrics.scss';

// How often the open panel re-reads its window (ms). Only a refresh cadence: the 30-min window
// itself is bounded by the server clock and comes back as window_s.
const REFRESH_MS = 30 * 1000;
const SPEED_LIMITS = [40, 60, 80];  // mph reference lines

// Card colours [t0, t1]: green below t0, yellow below t1, red at or above t1
// (merge distance is inverted: green above t0, yellow above t1, red at or below t1).
// Flow is vehicles PER MINUTE over the window. 3 / 6 are carried over from the tablet, which
// applies them to a different count, and are NOT yet confirmed for a per-minute rate
// (measured rates on the 2026-10-03 drives: 1.5-7.5 veh/min).
const FLOW_THRESHOLDS_VEH_PER_MIN = [3, 6];
const PASSING_SPEED_THRESHOLDS_MPH = [35, 50];
const MERGE_DISTANCE_THRESHOLDS_FT = [400, 200];

const getStatus = (value, thresholds, invert = false) => {
    if (value == null) return '';
    if (invert) {
        // Higher is better (distance): green > t[0], red <= t[1]
        if (value > thresholds[0]) return 'green';
        if (value > thresholds[1]) return 'yellow';
        return 'red';
    } else {
        // Lower is better (speed, flow): green < t[0], red >= t[1]
        if (value < thresholds[0]) return 'green';
        if (value < thresholds[1]) return 'yellow';
        return 'red';
    }
};

// One vehicle in 30 min is 0.03 veh/min: keep 2 decimals below 1 so it never reads as 0.
const formatRate = (r) => (r == null ? null : r < 1 ? r.toFixed(2) : r.toFixed(1));

// Server times: bucket keys are UTC 'YYYY-MM-DDTHH:MM'; range bounds are ISO with offset.
const clock = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const bucketLabel = (t) => clock(new Date(`${t}:00Z`));

const HeroCard = ({ label, value, unit, n, status }) => (
    <div className={`tm-hero-card${status ? ` tm-status-${status}` : ''}`}>
        <div className="tm-hero-value">{value != null ? value : '—'}</div>
        <div className="tm-hero-unit">{unit}</div>
        <div className="tm-hero-label">{label}</div>
        <div className="tm-hero-n">n = {n}</div>
    </div>
);

const LiveTraffic = ({ live, failed }) => {
    if (failed) return <p className="tm-live-empty">Live traffic is unavailable right now.</p>;
    if (!live) return <p className="tm-live-empty">Loading live traffic…</p>;

    const minutes = Math.round(live.window_s / 60);
    const t = live.tiles;
    if (!t.track_count) {
        // An empty window is not "zero traffic": nothing was measured to rate.
        return (
            <p className="tm-live-empty">
                {live.n_checkins
                    ? `No vehicles measured in the last ${minutes} min.`
                    : `No check-ins from this unit in the last ${minutes} min.`}
            </p>
        );
    }

    const chartData = live.series.passing_speed_mph.map(p => ({ time: bucketLabel(p.t), speed: p.avg, n: p.n }));
    return (
        <>
            <div className="tm-live-window">Last {minutes} min · as of {clock(new Date(live.range.end))}</div>
            <div className="tm-hero-cards">
                <HeroCard
                    label="Traffic Flow"
                    value={formatRate(live.vehicles_per_min)}
                    unit="veh/min"
                    n={t.track_count}
                    status={getStatus(live.vehicles_per_min, FLOW_THRESHOLDS_VEH_PER_MIN)}
                />
                <HeroCard
                    label="Passing Speed"
                    value={t.avg_passing_speed_mph}
                    unit="mph"
                    n={t.n_passing_speed}
                    status={getStatus(t.avg_passing_speed_mph, PASSING_SPEED_THRESHOLDS_MPH)}
                />
                <HeroCard
                    label="Merge Distance"
                    value={t.avg_merging_distance_ft}
                    unit="ft"
                    n={t.n_merging_distance}
                    status={getStatus(t.avg_merging_distance_ft, MERGE_DISTANCE_THRESHOLDS_FT, true)}
                />
            </div>

            <div className="tm-chart-section">
                <div className="tm-chart-title">Passing Speed — last {minutes} min, 5-min averages</div>
                {!t.n_passing_speed ? <p className="tm-live-empty">No passing speeds in this window.</p> :
                    <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={chartData} margin={{ top: 4, right: 12, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                            <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                            <Tooltip formatter={(v, name, entry) => [`${v} mph (n = ${entry.payload.n})`, 'Avg passing speed']} />
                            {SPEED_LIMITS.map(limit => (
                                <ReferenceLine
                                    key={limit}
                                    y={limit}
                                    stroke="rgba(220,53,69,0.55)"
                                    strokeDasharray="6 4"
                                    label={{ value: `${limit}`, position: 'insideTopRight', fontSize: 9, fill: 'rgba(220,53,69,0.75)' }}
                                />
                            ))}
                            {/* A 5-min bucket with no pass is a gap (avg null), not 0 mph; dots keep isolated buckets visible. */}
                            <Line
                                type="monotone"
                                dataKey="speed"
                                stroke="#4fc3f7"
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                isAnimationActive={false}
                                name="Avg Passing Speed"
                            />
                        </LineChart>
                    </ResponsiveContainer>}
            </div>
        </>
    );
};

const TrafficMetricsPanel = ({ item }) => {
    const dispatch = useDispatch();
    const serial = item?.serial_number;
    // live = the last successful read. A failed read clears it: the panel then says it is
    // unavailable instead of showing stale numbers or an empty (zero-traffic) window.
    const [live, setLive] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!serial) return undefined;
        let cancelled = false;
        setLive(null);
        setFailed(false);
        const load = () => dispatch(getLiveTraffic(serial)).then((action) => {
            if (cancelled) return;
            const ok = getLiveTraffic.fulfilled.match(action);
            setLive(ok ? action.payload : null);
            setFailed(!ok);
        });
        load();
        const timer = setInterval(load, REFRESH_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [dispatch, serial]);

    return (
        <div className="tm-panel">
            <LiveTraffic live={live} failed={failed} />
            <div className="tm-detail-link">
                <a className="metricsButton" rel="noreferrer" href={UPTIME_HISTORY} target="_blank">
                    Detailed Metrics
                </a>
            </div>
        </div>
    );
};

export default TrafficMetricsPanel;
